import { ReactNode } from "react";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import { arrowBack } from "ionicons/icons";
import { createRequestsPath, isPastRequestsRoute, SEGMENTS } from "routes/routeData";

type MobileViewWrapperProps = {
  title?: string | (() => JSX.Element);
  children: ReactNode;
};

export default function MobileViewWrapper(props: MobileViewWrapperProps) {
  const { children, title } = props;
  const router = useIonRouter();
  // default value provided to avoid Storybook test issues
  const { routeInfo = { pathname: "" } } = router;
  const pathSegments = routeInfo.pathname.substring(1).split("/");

  /**
   * Out of the box the IonBackButton shows if there is history
   * or if a defaultHref if provided. pathSegments.length > 1
   * checks to make sure we are not on the root route since we
   * do not want back navigation then.
   *
   * When creating a request, current logic is to navigate user
   * to the request details screen of the just created request.
   * We need to override the default back nav behavior in this
   * case since we don't want users going back to create a request
   * but rather take them to active requests index page.
   */
  return (
    <IonPage id="main-app">
      <IonHeader>
        <IonToolbar>
          {pathSegments.length > 1 &&
          router.routeInfo.lastPathname?.includes(SEGMENTS.requestASub) ? (
            <IonButtons slot="start">
              <IonButton
                data-testid="back-nav-request-a-sub"
                onClick={() => router.push(createRequestsPath("active-requests"))}
              >
                <IonIcon slot="icon-only" color="primary" icon={arrowBack}></IonIcon>
              </IonButton>
            </IonButtons>
          ) : pathSegments.length > 1 ? (
            <IonButtons slot="start">
              <IonBackButton
                data-testid="back-nav-non-root-route"
                color="primary"
                defaultHref={
                  isPastRequestsRoute(router.routeInfo.pathname)
                    ? createRequestsPath("past-requests")
                    : createRequestsPath("active-requests")
                }
              />
            </IonButtons>
          ) : (
            // note: when arriving on sub profile from activity log we are
            // using a link via. dangerouslySetInnerHTML which means there is
            // only 1 path segment so handling this case separately. The
            // pathname from the Activity Log is the 'active-requests' or
            // 'past-requests' so we need to use the lastPathname field
            // since that holds the path for the Request Details page
            window.location.hash.includes(SEGMENTS.subs) && (
              <IonButtons slot="start">
                <IonBackButton
                  data-testid="back-nav-sub-profile"
                  color="primary"
                  defaultHref={router.routeInfo.lastPathname}
                />
              </IonButtons>
            )
          )}
          {title && (
            <IonTitle>{typeof title === "string" ? <span>{title}</span> : <>{title()}</>}</IonTitle>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>{children}</IonContent>
    </IonPage>
  );
}
